import {
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  List,
  ShowButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";
import { CopyableEmailTag } from "components/fields/CopyableEmailTag";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import { IEntmVessels } from "interfaces";
import prettyBytes from "pretty-bytes";
import React from "react";

export const EntmVesselsList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, tableQuery: { isLoading } } = useTable<IEntmVessels>({
    resource: "entm-vessels"
  });

  const { selectProps: vesselSelectProps } = useSelect<IEntmVessels>({
    resource: "entm-vessels",
    optionLabel: "name",
    optionValue: "name",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: shipcoSelectProps } = useSelect<IEntmVessels>({
    resource: "entm-vessels",
    optionLabel: "shipco",
    optionValue: "shipco",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: imoNumberSelectProps } = useSelect<IEntmVessels>({
    resource: "entm-vessels",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",

    pagination: {
      mode: "server"
    }
  });

  const { triggerExport, isLoading: isLoadingExport } = useExport<IEntmVessels>({
    mapData: (item) => {
      return {
        "ID": item?.id,
        "Name": item?.name || "",
        "IMO Number": item?.imoNumber || "",
        "Shipco": item?.shipco || "",
        "Vessel Identifier": item?.vesselIdentifier || "",
        "Email": item?.email || "",
        "Alternate Email": item?.alternateEmail || "",
        "Max Email Size (Bytes)": item?.maxEmailSizeKb || "",
        "Max Transmission Size (MB)": item?.maxTransmissionSizeMb || "",
        "Hold Status": item?.holdStatus || "",
      };
    },
    unparseConfig: {
      header: true,
      columns: [
        "ID",
        "Name",
        "IMO Number",
        "Shipco",
        "Vessel Identifier",
        "Email",
        "Alternate Email",
        "Max Email Size (KB)",
        "Max Transmission Size (MB)",
        "Hold Status",
      ],
    },
  });

  const columns: ColumnsType<IEntmVessels> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select IMO Number"
            {...imoNumberSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Shipco",
      dataIndex: "shipco",
      key: "shipco",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Shipco"
            {...shipcoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 220,
      className: "wrap-text",
      render: (value) => <CopyableEmailTag value={value} />,
    },
    {
      title: "Max Email Size",
      dataIndex: "maxEmailSizeKb",
      key: "maxEmailSizeKb",
      width: 120,
      render: (value) => <TextField value={prettyBytes(value)} />,
    },
    {
      title: "Hold Status",
      dataIndex: "holdStatus",
      key: "holdStatus",
      width: 120,
      render: (value) => <TextField value={value} />,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        title: "eNTM Vessels",
        subTitle: "active eNTM vessels",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
